import paper from "paper";
import type {
  ConstructionLine,
  ConstructionPrimitive,
  Project,
} from "./Project";
import {
  isAutoGeneratedConstructionName,
  nextConstructionName,
} from "./Project";
import { effectiveConstructionLineKind } from "./constructionKind";
import { computeDefaultSmoothAnchors } from "./constructionAnchors";
import { pathDataEquivalent } from "./constructionPathData";
import { makeSlotPathData } from "../tools/CutoutTool";
import {
  propagateCoincidentAfterGroupMove,
  pruneInvalidCenterConstraints,
} from "./constraintEnforcement";

/** Re-derive a construction line's `kind` / `primitive` from its
 *  current pathData. A single-segment open path always normalises to
 *  one of two shapes — a parametric **Line** when both anchors have
 *  zero bezier handles, or a **Curve** (`kind: "freehand"`) when
 *  either anchor's handles are non-zero. Multi-segment paths and
 *  closed paths are left alone.
 *
 *  Called after every commit that mutates pathData so the user-
 *  facing label tracks the actual geometry: a fresh 2-point polyline
 *  draw becomes "Line", a segment-delete that opens a polyline down
 *  to one segment becomes "Line" (or "Curve" if that segment carries
 *  handles), and a bezier-cycle toggle on a Line flips it between
 *  Line ↔ Curve.
 *
 *  `proj` is consulted only for auto-name regeneration — when the
 *  existing name was auto-generated by an earlier commit, the new
 *  one comes from `nextConstructionName(proj, modifier)` so the
 *  panel label tracks the new shape. Pass `undefined` to skip the
 *  rename (the kind / primitive still change). */
export function canonicalizeConstructionLine(
  line: ConstructionLine,
  scope: paper.PaperScope,
  proj?: Project,
): ConstructionLine {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
  } catch {
    return line;
  }
  const subpaths: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  // Only single-subpath, open, 2-anchor paths normalise. Anything
  // else (compound paths, closed shapes, ≥ 3 anchors) keeps whatever
  // kind / primitive it already has.
  if (subpaths.length !== 1) {
    cp.remove();
    return line;
  }
  const sub = subpaths[0]!;
  if (sub.closed || sub.segments.length !== 2) {
    cp.remove();
    return line;
  }
  const s0 = sub.segments[0]!;
  const s1 = sub.segments[1]!;
  const HANDLE_EPS = 0.001;
  const hasHandles =
    Math.abs(s0.handleIn.x) > HANDLE_EPS ||
    Math.abs(s0.handleIn.y) > HANDLE_EPS ||
    Math.abs(s0.handleOut.x) > HANDLE_EPS ||
    Math.abs(s0.handleOut.y) > HANDLE_EPS ||
    Math.abs(s1.handleIn.x) > HANDLE_EPS ||
    Math.abs(s1.handleIn.y) > HANDLE_EPS ||
    Math.abs(s1.handleOut.x) > HANDLE_EPS ||
    Math.abs(s1.handleOut.y) > HANDLE_EPS;
  const p1 = { x: s0.point.x, y: s0.point.y };
  const p2 = { x: s1.point.x, y: s1.point.y };
  cp.remove();

  if (hasHandles) {
    // Two anchors + handles → no parametric Line primitive (a Line
    // is by definition straight). Drop the primitive; the
    // `deriveConstructionLineKinds` commit-pipeline pass will
    // recompute `kind` from the bezier geometry and rename any
    // auto-name into the matching modifier bucket ("Curve N"). A
    // primitive-free line already has nothing for this branch to
    // do.
    if (line.primitive === undefined) return line;
    return { ...line, primitive: undefined };
  }
  // No handles → Line primitive. If we're already a Line at the same
  // endpoints, leave the record alone (avoids spurious re-snapshots
  // / undo entries from the same drag).
  if (
    line.primitive?.kind === "line" &&
    Math.abs(line.primitive.x1 - p1.x) < 0.001 &&
    Math.abs(line.primitive.y1 - p1.y) < 0.001 &&
    Math.abs(line.primitive.x2 - p2.x) < 0.001 &&
    Math.abs(line.primitive.y2 - p2.y) < 0.001 &&
    line.kind === undefined
  ) {
    return line;
  }
  const next: ConstructionLine = {
    ...line,
    kind: undefined,
    primitive: {
      kind: "line",
      x1: p1.x,
      y1: p1.y,
      x2: p2.x,
      y2: p2.y,
    },
  };
  if (proj && isAutoGeneratedConstructionName(line.name)) {
    next.name = nextConstructionName(proj, "Line");
  }
  return next;
}

/** Apply a non-parametric edit to a construction line: drop the
 *  primitive metadata, set a fallback `kind`, and (depending on
 *  `opts.preserveOrigin`) either clear or preserve the snapshot
 *  needed to restore the original primitive identity later.
 *
 *  Line primitives get a special case: a vertex drag that only moves
 *  an endpoint keeps the geometry as a single straight segment, so
 *  the function detects that (no curve tokens + parseable pathData)
 *  and returns the line with its primitive intact — only the
 *  endpoints update. The drop / kind logic below only fires when the
 *  geometry actually changed to something a line can't represent.
 *
 *  Fallback kind (when the primitive really does drop):
 *  - rectangle / ellipse / pill → `"polygon"` (closed). These start
 *    closed; once the user warps them they become an editable closed
 *    polygon.
 *  - line + curve tokens in the new pathData (a curve-drag or
 *    bezier-handle drag bent it into an arc) → `"freehand"`
 *    (labelled "Curve" in the panel).
 *  - line with no curve tokens but pathData no longer matches the
 *    `M x y L x y` shape (delete-segment splitting a line into
 *    something exotic) → `"polyline"`.
 *
 *  - `opts.preserveOrigin = false` (default — destructive drag
 *    commits): clear `convertedFromPrimitive`. The line is
 *    permanently converted.
 *  - `opts.preserveOrigin = true` (reversible bezier-cycle commits):
 *    carry the primitive snapshot forward in `convertedFromPrimitive`
 *    and, when `opts.scope` is provided, check whether the live
 *    pathData matches the snapshot's natural pathData — if so,
 *    restore the primitive identity (re-deriving `smoothAnchors` so
 *    e.g. an ellipse comes back with its anchors marked smooth) and
 *    the original auto-generated name from the snapshot.
 *
 *  In both modes, the *first* conversion (when the line still has a
 *  live `primitive`) auto-renames an auto-generated name into the
 *  matching counter (Polyline / Polygon / Curve), so the user always
 *  sees a label that matches the current shape category. Hand-typed
 *  names are preserved throughout. */
export function dropConstructionPrimitive(
  line: ConstructionLine,
  proj: Project,
  patch: Partial<ConstructionLine>,
  opts?: { preserveOrigin?: boolean; scope?: paper.PaperScope },
): ConstructionLine {
  const preserveOrigin = opts?.preserveOrigin ?? false;
  const scope = opts?.scope;
  const wasPrimitive = line.primitive !== undefined;
  const newPathData = patch.pathData ?? line.pathData;
  // Geometry → kind mapping lives in one place
  // (`effectiveConstructionLineKind`) so a primitive dropped here
  // and a polyline-closed-to-polygon edit handled by the commit
  // pipeline's `deriveConstructionLineKinds` can't drift. Sets a
  // sensible kind on the dropped line; the commit-pipeline derive
  // pass refreshes it if `canonicalizeConstructionLine` below
  // promotes it back to a primitive (which clears `kind`) or any
  // future tweak changes the geometry.
  const droppedKind = effectiveConstructionLineKind(newPathData);
  const next: ConstructionLine = {
    ...line,
    ...patch,
    primitive: undefined,
    ...(wasPrimitive && !patch.kind ? { kind: droppedKind } : {}),
  };
  // Either the line still has its primitive (first conversion) or
  // it was already converted earlier and the snapshot lives on
  // `convertedFromPrimitive`. Same for the original name snapshot:
  // captured the first time we converted, then carried forward.
  const memoryPrimitive = wasPrimitive
    ? line.primitive
    : line.convertedFromPrimitive?.primitive;
  const memoryName = wasPrimitive
    ? isAutoGeneratedConstructionName(line.name)
      ? line.name
      : undefined
    : line.convertedFromPrimitive?.name;

  // Corner-rounding metadata (issue #100) describes how to derive
  // `pathData` from `sharpPathData`. A destructive edit feeds a
  // fresh `pathData` to this helper, which means the rounding
  // metadata's source no longer matches what the user sees — drop
  // it so the next render commits the new geometry as the new
  // source. Reversible (preserveOrigin) edits keep the metadata:
  // a cycle that round-trips back to the primitive shape also
  // round-trips back to the rounded shape, no surprises.
  if (!preserveOrigin && next.cornerRounding !== undefined) {
    next.cornerRounding = undefined;
  }

  // Freehand raw-source metadata (issue #171). A destructive edit fed through
  // here makes the new `pathData` the source of truth, so the derive pass has
  // to stand down or the next commit would rebuild the line from the trace and
  // undo the edit. It used to drop the trace outright, which also retired the
  // Smoothness slider for good; #248 keeps the trace and only marks it, so the
  // slider can still be re-applied afterwards — at the cost of the hand edits,
  // which is the trade the panel spells out next to it.
  if (!preserveOrigin && next.rawPoints !== undefined) {
    next.freehandEdited = true;
  }

  // User-positioned rotation pivot (issue #420) — same lifecycle as
  // the metadata above. A destructive reshape changes the natural
  // frame the pivot was placed against, so drop it; the effective
  // pivot falls back to the new bbox centre. Reversible edits keep it.
  if (!preserveOrigin && next.pivotPoint !== undefined) {
    next.pivotPoint = undefined;
  }

  if (preserveOrigin) {
    // Reversible edit — try to restore if geometry matches the
    // snapshot; otherwise stash the snapshot for a future try.
    if (memoryPrimitive && scope) {
      let expected: string | null = null;
      try {
        expected = rebuildPrimitivePath(memoryPrimitive, scope);
      } catch {
        expected = null;
      }
      // Canonicalise both sides through paper.js — `next.pathData`
      // comes from paper's serializer (after a cycle commit), while
      // `expected` may be a hand-rolled string from
      // `rebuildPrimitivePath` (rect uses a custom format). Without
      // this, the string compare almost never matches even when the
      // geometries are identical.
      if (
        expected !== null &&
        pathDataEquivalent(next.pathData, expected, scope)
      ) {
        next.primitive = memoryPrimitive;
        next.kind = undefined;
        next.convertedFromPrimitive = undefined;
        // Re-derive smoothAnchors so e.g. an ellipse comes back
        // with all four anchors marked smooth (matches creation).
        next.smoothAnchors = computeDefaultSmoothAnchors(next.pathData, scope);
        if (memoryName !== undefined) next.name = memoryName;
        return next;
      }
    }
    if (memoryPrimitive) {
      next.convertedFromPrimitive = {
        primitive: memoryPrimitive,
        ...(memoryName !== undefined ? { name: memoryName } : {}),
      };
    }
  } else {
    next.convertedFromPrimitive = undefined;
  }

  // Rename auto-named lines so the user-visible label matches the
  // resulting shape category. Two triggers:
  //  - First-time conversion (line was still a primitive going in).
  //  - Polygon → polyline transition (an already-converted polygon
  //    just had a segment / anchor deleted that opened the path),
  //    so the label flips from "Polygon N" to "Polyline N".
  // Hand-typed names are left alone in both cases.
  const becamePolyline =
    !wasPrimitive && line.kind === "polygon" && next.kind === "polyline";
  if (
    (wasPrimitive || becamePolyline) &&
    isAutoGeneratedConstructionName(line.name)
  ) {
    const modifier =
      next.kind === "polyline"
        ? "Polyline"
        : next.kind === "freehand"
          ? "Curve"
          : "Polygon";
    next.name = nextConstructionName(proj, modifier);
  }
  // Final pass: a single-segment open path is always a Line (no
  // handles) or a Curve (handles). The drop logic above may have
  // landed us at "polyline"/"freehand" with no primitive — promote
  // back to the structured shape so the panel header / inputs match
  // the geometry. Skipped when scope wasn't supplied (the canonical
  // helper needs paper.js to parse the pathData). The reversible
  // bezier-cycle path may have already restored an *original*
  // primitive — re-running canonicalization is still safe because it
  // no-ops when the line is already canonical.
  if (scope) {
    return canonicalizeConstructionLine(next, scope, proj);
  }
  return next;
}

/** Pick the human-readable modifier (e.g. "Square") for a primitive
 *  so the tool can name the construction line accordingly. Square /
 *  circle are detected when the rect/ellipse's two axes are equal
 *  within float-fuzz tolerance. */
export function modifierFor(primitive: ConstructionPrimitive): string {
  if (primitive.kind === "rectangle") {
    return Math.abs(primitive.width - primitive.height) < 0.001
      ? "Square"
      : "Rectangle";
  }
  if (primitive.kind === "ellipse") {
    return Math.abs(primitive.width - primitive.height) < 0.001
      ? "Circle"
      : "Ellipse";
  }
  if (primitive.kind === "line") {
    return "Line";
  }
  return "Pill";
}

/** Rebuild the SVG path data for a parametric construction
 *  primitive from its current params. Used by the construction-line
 *  dimension panel after the user edits a width / height / length
 *  input AND by `dropConstructionPrimitive`'s reversible-edit path
 *  to detect when a polygon's geometry has come back to match its
 *  original primitive's natural shape. */
export function rebuildPrimitivePath(
  prim: NonNullable<ConstructionLine["primitive"]>,
  scope: paper.PaperScope,
): string {
  if (prim.kind === "rectangle") {
    const right = prim.x + prim.width;
    const bottom = prim.y + prim.height;
    return `M${prim.x} ${prim.y} L${right} ${prim.y} L${right} ${bottom} L${prim.x} ${bottom} Z`;
  }
  if (prim.kind === "ellipse") {
    const tmp = new scope.Path.Ellipse(
      new scope.Rectangle(
        new scope.Point(prim.x, prim.y),
        new scope.Size(prim.width, prim.height),
      ),
    );
    const data = tmp.pathData;
    tmp.remove();
    return data;
  }
  if (prim.kind === "line") {
    return `M${prim.x1} ${prim.y1} L${prim.x2} ${prim.y2}`;
  }
  return (
    makeSlotPathData(
      { x: prim.x1, y: prim.y1 },
      { x: prim.x2, y: prim.y2 },
      prim.width,
    ) ?? ""
  );
}

/** True when `after` is `before` moved rigidly: same segment count, same
 *  handles, every point shifted by one delta. A snap chain only translates
 *  or pulls anchors, so anything else means an anchor was pulled. */
function isTranslationOf(
  before: string,
  after: string,
  scope: paper.PaperScope,
): boolean {
  if (before === after) return true;
  const EPS = 1e-6;
  const a = new scope.CompoundPath({ pathData: before, insert: false });
  const b = new scope.CompoundPath({ pathData: after, insert: false });
  const subs = (cp: paper.CompoundPath) =>
    (cp.children.length > 0 ? cp.children : [cp]) as paper.Path[];
  const sa = subs(a);
  const sb = subs(b);
  if (sa.length !== sb.length) return false;
  let dx: number | null = null;
  let dy = 0;
  for (let i = 0; i < sa.length; i++) {
    const pa = sa[i]!.segments;
    const pb = sb[i]!.segments;
    if (pa.length !== pb.length) return false;
    for (let j = 0; j < pa.length; j++) {
      const u = pa[j]!;
      const v = pb[j]!;
      if (dx === null) {
        dx = v.point.x - u.point.x;
        dy = v.point.y - u.point.y;
      }
      if (
        Math.abs(v.point.x - u.point.x - dx) > EPS ||
        Math.abs(v.point.y - u.point.y - dy) > EPS ||
        !v.handleIn.subtract(u.handleIn).isZero() ||
        !v.handleOut.subtract(u.handleOut).isZero()
      ) {
        return false;
      }
    }
  }
  return true;
}

/** Finish a snap enforcement that may have reshaped lines.
 *
 *  Snap enforcement translates partners and, to keep every link attached,
 *  moves single anchors: on a partner reached through two of its links
 *  (chain-through-partner, #1166) and, during a vertex drag or an Apply,
 *  on the origin itself (chain-through-origin). Those pulls edit only
 *  `pathData`, so a rectangle / ellipse / rounded line would keep a
 *  `primitive` or `cornerRounding` describing a shape it no longer has —
 *  and the rounding pass would revert the pull. For every line that
 *  `enforced` changed by more than a translation relative to `base`:
 *   - a line primitive keeps its identity with its endpoints updated;
 *   - anything else goes through `dropConstructionPrimitive` (the rule
 *     vertex drag follows: the shape distorts and stops claiming to be a
 *     primitive).
 *  Then the same center / edge-midpoint prune vertex drag runs (a pulled
 *  closed shape keeps those snaps, #417).
 *
 *  `base` is the line list enforcement started from (already holding the
 *  user's own edit, which is therefore never touched); `enforced` is its
 *  result. Returns the project to commit. */
export function settleSnapReshapedLines(
  proj: Project,
  base: ConstructionLine[],
  enforced: ConstructionLine[],
  scope: paper.PaperScope,
): Project {
  const baseById = new Map(base.map((l) => [l.id, l]));
  const lines = enforced.map((l) => {
    const was = baseById.get(l.id);
    if (!was || l === was) return l;
    if (!l.primitive && !l.cornerRounding) return l;
    if (isTranslationOf(was.pathData, l.pathData, scope)) return l;
    if (l.primitive?.kind === "line") {
      const cp = new scope.CompoundPath({
        pathData: l.pathData,
        insert: false,
      });
      const sub = (cp.children[0] ?? cp) as paper.Path;
      const first = sub.firstSegment?.point;
      const last = sub.lastSegment?.point;
      if (first && last) {
        return {
          ...l,
          primitive: {
            ...l.primitive,
            x1: first.x,
            y1: first.y,
            x2: last.x,
            y2: last.y,
          },
        };
      }
    }
    return dropConstructionPrimitive(
      l,
      proj,
      { pathData: l.pathData },
      { scope },
    );
  });
  return {
    ...proj,
    constructionLines: lines,
    constraints: pruneInvalidCenterConstraints(lines, proj.constraints),
  };
}

/** `base` (already holding the user's edit to the lines in `editedIds` — a
 *  resize, a rotation, a flip) with every snapped partner following, as the
 *  project to commit.
 *
 *  The edited lines are pinned: their new shape is final. Every anchor of
 *  every edited line, plus the center and edge midpoints of a primitive,
 *  drives the partners, each reading its ref's CURRENT position. A partner
 *  linked at one point moves; one linked at two or more points stretches so
 *  every link stays attached; a partner that stops matching its primitive
 *  is settled (`settleSnapReshapedLines`). The one rule every whole-line
 *  reshape uses, so they can't drift apart (#1166). */
export function withSnappedPartnersFollowing(
  proj: Project,
  base: ConstructionLine[],
  editedIds: Iterable<string>,
  scope: paper.PaperScope,
): Project {
  const enforced = propagateCoincidentAfterGroupMove(
    proj,
    base,
    [...editedIds],
    scope,
  );
  return settleSnapReshapedLines(proj, base, enforced, scope);
}
